import { useCallback, useEffect, useRef, useState } from 'react';

import {
	type AskRovoTrigger,
	getRovoChatClosedEvent,
	getRovoChatSwitchedEvent,
} from '../askRovoAnalytics.ts';
import { clearSeededRovoPrompt, useSeededRovoPrompt } from '../store/seededRovoPromptBridge.ts';
import { useTwgAnalyticsEvents } from '../useTwgAnalyticsEvents.ts';

type PanelMode = 'connections' | 'rovo';

export type UseTeamworkGraphPreviewPanelModeOptions = {
	isRovoEnabled: boolean;
	/** Scopes the seeded-prompt bridge read, so a stale publish cannot open the wrong page's chat. */
	contentId?: string;
};

export type TeamworkGraphPreviewPanelMode = {
	isRovo: boolean;
	prefilledPrompt: string | undefined;
	showRovo: (trigger: AskRovoTrigger, prompt?: string) => void;
	showConnections: () => void;
};

/**
 * What the Teamwork Graph preview panel's body is showing, and the ways in and out of Rovo.
 *
 * The panel's counterpart to `useTeamworkGraphModalMode` — the same shape without the close reset,
 * which is the modal's concern. Rovo replaces the connections in place rather than opening the
 * full-size modal, so the chat stays in the panel already open and there is nothing to close.
 *
 * `isRovoEnabled` decides only whether the chat may show. Whether to OFFER it is the caller's to
 * gate, so that a site without Rovo loses the button rather than the footer around it.
 *
 * Unlike the modal, the panel has no single `close` handler to wrap: the header's close, Esc and a
 * route change all dismiss it by unmounting the panel, not by routing through this hook. So the
 * `panelClose` exit is caught on unmount instead — the panel only mounts while open, so unmounting
 * while still in Rovo is a dismissal straight out of the chat, the panel's parallel to the modal's
 * `modalClose`. Leaving via the back button flips the mode to connections first, so that path
 * unmounts in connections mode and does not double-count.
 */
export const useTeamworkGraphPreviewPanelMode = ({
	isRovoEnabled,
	contentId,
}: UseTeamworkGraphPreviewPanelModeOptions): TeamworkGraphPreviewPanelMode => {
	const { createAnalyticsEvent } = useTwgAnalyticsEvents();
	const [mode, setMode] = useState<PanelMode>('connections');
	const [prefilledPrompt, setPrefilledPrompt] = useState<string | undefined>(undefined);
	// The control that opened the chat, remembered so the close event can report it too.
	const [openTrigger, setOpenTrigger] = useState<AskRovoTrigger | undefined>(undefined);

	const showRovo = useCallback(
		(trigger: AskRovoTrigger, prompt?: string) => {
			createAnalyticsEvent(getRovoChatSwitchedEvent({ surface: 'panel', trigger })).fire();
			setOpenTrigger(trigger);
			setPrefilledPrompt(prompt);
			setMode('rovo');
		},
		[createAnalyticsEvent],
	);

	// A prompt published by the byline popup's share-context modal. Read as the mode rather than
	// copied into it, so there is no effect setting state from state. The bridge is therefore the
	// session's source of truth, which is why `showConnections` and unmount both clear it.
	const { prompt: seededPrompt } = useSeededRovoPrompt(contentId);
	const isSeeded = seededPrompt !== null;
	const activeMode = isSeeded ? 'rovo' : mode;
	const activeTrigger: AskRovoTrigger | undefined = isSeeded ? 'shareContext' : openTrigger;

	// The seeded entry has no `showRovo` call to carry its `switched` event, so it fires here —
	// once per session, per the ref.
	const hasFiredShareContextSwitchRef = useRef(false);
	const createAnalyticsEventRef = useRef(createAnalyticsEvent);
	createAnalyticsEventRef.current = createAnalyticsEvent;
	useEffect(() => {
		if (!isSeeded || !isRovoEnabled || hasFiredShareContextSwitchRef.current) {
			return;
		}
		hasFiredShareContextSwitchRef.current = true;
		createAnalyticsEventRef
			.current(getRovoChatSwitchedEvent({ surface: 'panel', trigger: 'shareContext' }))
			.fire();
	}, [isSeeded, isRovoEnabled]);

	// Only the Rovo chat's back button calls this, so it always leaves the chat.
	const showConnections = useCallback(() => {
		createAnalyticsEvent(
			getRovoChatClosedEvent({ surface: 'panel', method: 'back', trigger: activeTrigger }),
		).fire();
		clearSeededRovoPrompt();
		hasFiredShareContextSwitchRef.current = false;
		setOpenTrigger(undefined);
		// Cleared on the way out, or it would reappear the next time Rovo is opened — including
		// from an entry meant to open empty, like a row whose link has no url or "At a glance"
		// when there are no connection links to seed.
		setPrefilledPrompt(undefined);
		setMode('connections');
	}, [createAnalyticsEvent, activeTrigger]);

	// Read through refs from the unmount cleanup so it fires with the mode (and opening trigger) at
	// dismissal without re-subscribing (and re-running the cleanup) on every mode change or update.
	const showingRovoRef = useRef(false);
	showingRovoRef.current = isRovoEnabled && activeMode === 'rovo';
	const activeTriggerRef = useRef(activeTrigger);
	activeTriggerRef.current = activeTrigger;
	useEffect(
		() => () => {
			if (showingRovoRef.current) {
				createAnalyticsEventRef
					.current(
						getRovoChatClosedEvent({
							surface: 'panel',
							method: 'panelClose',
							trigger: activeTriggerRef.current,
						}),
					)
					.fire();
			}
			// The bridge outlives the panel, so an unconsumed prompt has to go with it.
			clearSeededRovoPrompt();
		},
		[],
	);

	return {
		// Entitlement is checked alongside the mode, as the modal does, so losing Rovo falls back
		// to the connections rather than stranding the user on a chat they cannot use.
		isRovo: isRovoEnabled && activeMode === 'rovo',
		prefilledPrompt: isSeeded ? seededPrompt : prefilledPrompt,
		showRovo,
		showConnections,
	};
};
